import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from './http';
import type { DepartmentDto, GroupListItem, PagedResult, UserListItem, UserProfile } from './types';

/*
 * Directory endpoints used by shared pickers (ARCHITECTURE §4.10). Users & departments belong to the
 * `company` frontend module, groups to `groups` — hence the key roots.
 */

export interface UserSearchParams {
  search?: string;
  departmentId?: string;
  page?: number;
  pageSize?: number;
  includeInactive?: boolean;
}

export interface GroupSearchParams {
  search?: string;
  mine?: boolean;
  page?: number;
  pageSize?: number;
}

export const directoryKeys = {
  users: ['company', 'users'] as const,
  userSearch: (params: UserSearchParams) => ['company', 'users', 'search', params] as const,
  user: (id: string) => ['company', 'users', 'detail', id] as const,
  departments: ['company', 'departments'] as const,
  groups: ['groups'] as const,
  groupSearch: (params: GroupSearchParams) => ['groups', 'search', params] as const,
};

export const fetchUsers = (params: UserSearchParams, signal?: AbortSignal) =>
  api.get<PagedResult<UserListItem>>('/users', { query: { ...params }, signal });

export const fetchUser = (id: string, signal?: AbortSignal) =>
  api.get<UserProfile>(`/users/${encodeURIComponent(id)}`, { signal });

export const fetchDepartments = (signal?: AbortSignal) =>
  api.get<DepartmentDto[]>('/departments', { signal });

export const fetchGroups = (params: GroupSearchParams, signal?: AbortSignal) =>
  api.get<PagedResult<GroupListItem>>('/groups', { query: { ...params }, signal });

/** Debounce the search text before passing it in. */
export function useUserSearch(params: UserSearchParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: directoryKeys.userSearch(params),
    queryFn: ({ signal }) => fetchUsers(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useUser(id: string | null | undefined) {
  return useQuery({
    queryKey: directoryKeys.user(id ?? ''),
    queryFn: ({ signal }) => fetchUser(id ?? '', signal),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useDepartments(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: directoryKeys.departments,
    queryFn: ({ signal }) => fetchDepartments(signal),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useGroupSearch(params: GroupSearchParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: directoryKeys.groupSearch(params),
    queryFn: ({ signal }) => fetchGroups(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export interface DepartmentNode extends DepartmentDto {
  depth: number;
  children: DepartmentNode[];
}

/** Builds the department forest from the flat list (sorted by sortOrder, then name). Orphans become roots. */
export function buildDepartmentTree(
  departments: readonly DepartmentDto[],
  locale?: string,
): DepartmentNode[] {
  const nodes = new Map<string, DepartmentNode>();
  for (const d of departments) nodes.set(d.id, { ...d, depth: 0, children: [] });
  const roots: DepartmentNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  const compare = (a: DepartmentNode, b: DepartmentNode) =>
    a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, locale);
  const visit = (list: DepartmentNode[], depth: number, seen: Set<string>) => {
    list.sort(compare);
    for (const node of list) {
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      node.depth = depth;
      visit(node.children, depth + 1, seen);
    }
  };
  const seen = new Set<string>();
  visit(roots, 0, seen);
  // Nodes caught in a parent cycle are unreachable from any root: surface them as roots.
  for (const node of nodes.values()) {
    if (seen.has(node.id)) continue;
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children = parent.children.filter((c) => c !== node);
    roots.push(node);
    visit([node], 0, seen);
  }
  roots.sort(compare);
  return roots;
}

/** Depth-first flattening of the tree (for lists with indentation). */
export function flattenDepartmentTree(roots: readonly DepartmentNode[]): DepartmentNode[] {
  const result: DepartmentNode[] = [];
  const walk = (list: readonly DepartmentNode[]) => {
    for (const node of list) {
      result.push(node);
      walk(node.children);
    }
  };
  walk(roots);
  return result;
}
